import type { DriftCategory } from "../../../api/companySlugDrift.service.js";

/**
 * Parses a rollback CSV produced by buildRollbackCsv back into apply rows.
 *
 * This is the input to a WRITE that restores earlier values verbatim, so the parser is
 * deliberately strict: a malformed file must fail loudly here rather than reach the API
 * as a half-understood row set. Every rejection carries its line number so an operator can
 * find it in the file.
 */

export type ParsedRollbackRow = {
  officeId: string;
  category: DriftCategory;
  proposed: string;
  companyName: string;
};

export type ParseResult = {
  rows: ParsedRollbackRow[];
  errors: string[];
};

const CATEGORIES: DriftCategory[] = ["company_slug", "company_name", "office_slug_prefix"];

/** Splits one CSV line, honouring double-quoted cells and "" escapes. */
function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      cells.push(cell);
      cell = "";
    } else {
      cell += ch;
    }
  }
  cells.push(cell);
  return cells;
}

export function parseRollbackCsv(text: string): ParseResult {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== "");
  const errors: string[] = [];

  if (lines.length === 0) return { rows: [], errors: ["The file is empty."] };

  const header = splitCsvLine(lines[0]).map(h => h.trim());
  const expected = ["officeId", "category", "proposed", "companyName"];
  if (header.join(",") !== expected.join(",")) {
    return {
      rows: [],
      errors: [`Unexpected header. Expected "${expected.join(",")}", got "${header.join(",")}".`],
    };
  }

  const rows: ParsedRollbackRow[] = [];
  const seen = new Set<string>();

  for (let i = 1; i < lines.length; i++) {
    const lineNo = i + 1;
    const cells = splitCsvLine(lines[i]);
    if (cells.length !== 4) {
      errors.push(`Line ${lineNo}: expected 4 columns, got ${cells.length}.`);
      continue;
    }

    const [officeId, category, proposed, companyName] = cells.map(c => c.trim());

    if (!officeId) {
      errors.push(`Line ${lineNo}: missing officeId.`);
      continue;
    }
    if (!CATEGORIES.includes(category as DriftCategory)) {
      errors.push(`Line ${lineNo}: unknown category "${category}".`);
      continue;
    }
    // An empty cell is legitimate: it means the column was empty before the run, and
    // restore writes it back as NULL. Rejecting it would make the rows people repair most
    // — an empty Company name copy — impossible to undo.

    const key = `${officeId}:${category}`;
    if (seen.has(key)) {
      errors.push(`Line ${lineNo}: duplicate row for this office and category.`);
      continue;
    }
    seen.add(key);

    rows.push({
      officeId,
      category: category as DriftCategory,
      proposed,
      companyName,
    });
  }

  return { rows, errors };
}
